import { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faColumns } from '@fortawesome/free-solid-svg-icons';

export interface ColumnDef {
  key: string;
  label: string;
}

interface ColumnVisibilityMenuProps {
  columns: ColumnDef[];
  hidden: Set<string>;
  onToggle: (key: string) => void;
  onReset?: () => void;
}

export default function ColumnVisibilityMenu({
  columns,
  hidden,
  onToggle,
  onReset,
}: ColumnVisibilityMenuProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  const visibleCount = columns.length - hidden.size;

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white hover:bg-gray-50"
        aria-haspopup="true"
        aria-expanded={open}
      >
        <FontAwesomeIcon icon={faColumns} className="text-gray-500" />
        <span>Columns</span>
        <span className="text-gray-500">({visibleCount}/{columns.length})</span>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-1"
        >
          <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase border-b">
            Show columns
          </div>
          <div className="max-h-72 overflow-y-auto py-1">
            {columns.map((col) => {
              const isVisible = !hidden.has(col.key);
              return (
                <label
                  key={col.key}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={isVisible}
                    onChange={() => onToggle(col.key)}
                    className="rounded border-gray-300"
                  />
                  <span>{col.label}</span>
                </label>
              );
            })}
          </div>
          {onReset && (
            <div className="border-t px-2 py-1">
              <button
                type="button"
                onClick={() => {
                  onReset();
                  setOpen(false);
                }}
                className="w-full text-left px-2 py-1 text-sm text-blue-600 hover:bg-blue-50 rounded"
              >
                Reset to defaults
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
